import { formatDistanceToNow } from 'date-fns';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  RefreshCw,
  Share2,
  ThumbsUp,
  User,
} from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { LinkedinIcon } from '../../../components/icons/BrandIcons';
import { Avatar, Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DATE_LOCALES } from '../../../lib/dateLocales';
import { colors } from '../../../theme/colors';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { byPostRecencyDesc } from '../utils/postRecency';
import { CommentRow } from './CommentRow';
import type { LinkedInPostData } from './LinkedInPostsCard';
import {
  EngagementDisclosure,
  EngagementPanel,
  EnrichButton,
  ReactionGlyph,
} from './PostEngagement';

interface PostSource {
  type: 'company' | 'contact';
  name: string;
  avatarName: string;
  sourceUrl?: string | null;
  isPrimary?: boolean;
}

interface SourcePage {
  source: PostSource;
  posts: LinkedInPostData[];
}

export type RefreshTarget =
  | { type: 'all' }
  | { type: 'company' }
  | { type: 'contact'; sourceUrl: string; name: string };

interface LinkedInActivityCarouselCardProps {
  companyName?: string | null;
  companyPosts?: LinkedInPostData[];
  contacts?: Array<{
    name: string | null;
    sourceUrl?: string | null;
    isPrimary?: boolean;
    posts: LinkedInPostData[];
  }>;
  onRefresh?: (target: RefreshTarget) => void;
  isRefreshing?: boolean;
  onEnrichCommenter?: (profileUrl: string, name: string) => void;
}

function PostImages({ images }: Readonly<{ images: string[] }>) {
  if (images.length === 1) {
    return (
      <Image
        source={{ uri: images[0] }}
        style={{
          width: '100%',
          height: 160,
          borderRadius: tokens.radius.md,
        }}
        resizeMode="cover"
        accessibilityLabel="Post image"
      />
    );
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <HStack spacing="sm">
        {images.map((uri, i) => (
          <Image
            key={uri}
            source={{ uri }}
            style={{
              width: 120,
              height: 90,
              borderRadius: tokens.radius.md,
            }}
            resizeMode="cover"
            accessibilityLabel={`Post image ${i + 1}`}
          />
        ))}
      </HStack>
    </ScrollView>
  );
}

function TimelinePost({
  post,
  isLast,
  onEnrichCommenter,
}: Readonly<{
  post: LinkedInPostData;
  isLast: boolean;
  onEnrichCommenter?: (profileUrl: string, name: string) => void;
}>) {
  const { t, language } = useTranslation();
  const [textExpanded, setTextExpanded] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [reactionsExpanded, setReactionsExpanded] = useState(false);
  const truncated = post.text.length > 200;

  const handleOpenUrl = useCallback(() => {
    if (!post.url) return;
    try {
      const parsed = new URL(post.url);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return;
      if (Platform.OS === 'web') {
        window.open(post.url, '_blank', 'noopener,noreferrer');
      } else {
        Linking.openURL(post.url);
      }
    } catch {
      // invalid URL
    }
  }, [post.url]);

  const postedAgo = post.postedAt
    ? formatDistanceToNow(new Date(post.postedAt), {
        addSuffix: true,
        locale: DATE_LOCALES[language],
      })
    : '';

  return (
    <HStack spacing="sm" style={{ minHeight: 40 }}>
      {/* Timeline line + dot */}
      <View style={styles.timelineTrack}>
        <View style={styles.timelineDot} />
        {!isLast && <View style={styles.timelineLine} />}
      </View>

      {/* Post content */}
      <VStack spacing="xs" style={{ flex: 1, paddingBottom: isLast ? 0 : tokens.spacing.md }}>
        {/* Timestamp + open link */}
        <HStack spacing="xs" align="center" justify="between">
          {postedAgo ? (
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {postedAgo}
            </Text>
          ) : (
            <View />
          )}
          {Boolean(post.url) && (
            <Pressable
              onPress={handleOpenUrl}
              accessibilityLabel="Open post on LinkedIn"
              style={styles.linkedinButton}
            >
              <LinkedinIcon size={12} color={colors.accent.blue[400]} strokeWidth={2} />
            </Pressable>
          )}
        </HStack>

        {/* Post text */}
        {Boolean(post.text) && (
          <VStack spacing="xxs">
            <Text variant="caption" color={colors.text.secondary} style={{ lineHeight: 17 }}>
              {textExpanded || !truncated ? post.text : `${post.text.substring(0, 200)}...`}
            </Text>
            {truncated && (
              <Pressable onPress={() => setTextExpanded(!textExpanded)}>
                <Text variant="caption" color={colors.primary[400]} style={{ fontWeight: '500' }}>
                  {textExpanded
                    ? t('leads.detail.linkedinPosts.showLess')
                    : t('leads.detail.linkedinPosts.showMore')}
                </Text>
              </Pressable>
            )}
          </VStack>
        )}

        {/* Images */}
        {post.images && post.images.length > 0 && <PostImages images={post.images} />}

        {/* Engagement bar */}
        <HStack spacing="md" align="center" style={{ paddingTop: 2 }}>
          <HStack spacing="xs" align="center">
            <ThumbsUp size={11} color={colors.accent.blue[400]} strokeWidth={1.5} />
            <Text variant="caption" color={colors.text.tertiary} style={{ fontWeight: '500' }}>
              {post.numLikes}
            </Text>
          </HStack>
          <HStack spacing="xs" align="center">
            <MessageCircle size={11} color={colors.text.tertiary} strokeWidth={1.5} />
            <Text variant="caption" color={colors.text.tertiary} style={{ fontWeight: '500' }}>
              {post.numComments}
            </Text>
          </HStack>
          {post.numShares != null && post.numShares > 0 && (
            <HStack spacing="xs" align="center">
              <Share2 size={11} color={colors.text.tertiary} strokeWidth={1.5} />
              <Text variant="caption" color={colors.text.tertiary} style={{ fontWeight: '500' }}>
                {post.numShares}
              </Text>
            </HStack>
          )}
        </HStack>

        {/* Comments (collapsible) */}
        {post.comments && post.comments.length > 0 && (
          <VStack spacing="none">
            <EngagementDisclosure
              expanded={commentsExpanded}
              onToggle={() => setCommentsExpanded(!commentsExpanded)}
              faces={post.comments.map((c) => ({
                name: c.authorName,
                imageUrl: c.authorProfilePicture,
              }))}
              label={`${t('leads.detail.linkedinPosts.comments')} (${post.comments.length})`}
              leading={<MessageCircle size={12} color={colors.text.tertiary} strokeWidth={2} />}
            />
            {commentsExpanded && (
              <EngagementPanel>
                {post.comments.slice(0, 5).map((comment, i) => (
                  <CommentRow
                    key={`${comment.authorName}-${i}`}
                    authorName={comment.authorName}
                    authorImageUrl={comment.authorProfilePicture}
                    meta={comment.authorHeadline}
                    body={comment.text}
                    action={
                      comment.authorProfileUrl && onEnrichCommenter ? (
                        <EnrichButton
                          profileUrl={comment.authorProfileUrl}
                          name={comment.authorName}
                          onEnrich={onEnrichCommenter}
                        />
                      ) : undefined
                    }
                  />
                ))}
                {post.comments.length > 5 && (
                  <Text variant="caption" color={colors.text.tertiary}>
                    {`+${post.comments.length - 5} ${t('leads.detail.linkedinPosts.more')}`}
                  </Text>
                )}
              </EngagementPanel>
            )}
          </VStack>
        )}

        {/* Reactions (collapsible) */}
        {post.reactions && post.reactions.length > 0 && (
          <VStack spacing="none">
            <EngagementDisclosure
              expanded={reactionsExpanded}
              onToggle={() => setReactionsExpanded(!reactionsExpanded)}
              faces={post.reactions.map((r) => ({
                name: r.authorName,
                imageUrl: r.authorProfilePicture,
              }))}
              label={`${t('leads.detail.linkedinPosts.reactions')} (${post.reactions.length})`}
              leading={<ThumbsUp size={12} color={colors.text.tertiary} strokeWidth={2} />}
            />
            {reactionsExpanded && (
              <EngagementPanel>
                {post.reactions.slice(0, 10).map((reaction, i) => (
                  <CommentRow
                    key={`${reaction.authorName}-${i}`}
                    authorName={reaction.authorName}
                    authorImageUrl={reaction.authorProfilePicture}
                    meta={reaction.authorHeadline}
                    action={
                      reaction.authorProfileUrl && onEnrichCommenter ? (
                        <EnrichButton
                          profileUrl={reaction.authorProfileUrl}
                          name={reaction.authorName}
                          onEnrich={onEnrichCommenter}
                        />
                      ) : undefined
                    }
                    trailing={<ReactionGlyph type={reaction.type} size={13} />}
                  />
                ))}
                {post.reactions.length > 10 && (
                  <Text variant="caption" color={colors.text.tertiary}>
                    {`+${post.reactions.length - 10} ${t('leads.detail.linkedinPosts.more')}`}
                  </Text>
                )}
              </EngagementPanel>
            )}
          </VStack>
        )}
      </VStack>
    </HStack>
  );
}

function SourcePageSlide({
  page,
  onEnrichCommenter,
}: Readonly<{
  page: SourcePage;
  onEnrichCommenter?: (profileUrl: string, name: string) => void;
}>) {
  const isCompany = page.source.type === 'company';

  return (
    <VStack spacing="sm">
      {/* Source header */}
      <HStack spacing="sm" align="center">
        <Avatar size="md" name={page.source.avatarName} />
        <VStack spacing="xxs" style={{ flex: 1, minWidth: 0 }}>
          <Text
            variant="bodySmall"
            color={colors.text.primary}
            style={{ fontWeight: '600' }}
            numberOfLines={1}
          >
            {page.source.name}
          </Text>
          <HStack spacing="xs" align="center">
            <Badge
              variant="subtle"
              color={isCompany ? 'amber' : page.source.isPrimary ? 'green' : 'blue'}
              size="sm"
            >
              {isCompany ? 'Company' : page.source.isPrimary ? 'Primary Contact' : 'Contact'}
            </Badge>
            <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
              {page.posts.length} {page.posts.length === 1 ? 'post' : 'posts'}
            </Text>
          </HStack>
        </VStack>
      </HStack>

      {/* Timeline of posts */}
      <ScrollView
        style={styles.timelineScroll}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
      >
        <VStack>
          {page.posts.map((post, i) => (
            <TimelinePost
              key={post.url || `post-${i}`}
              post={post}
              isLast={i === page.posts.length - 1}
              onEnrichCommenter={onEnrichCommenter}
            />
          ))}
        </VStack>
      </ScrollView>
    </VStack>
  );
}

function PaginationDots({
  count,
  activeIndex,
  onDotPress,
}: Readonly<{
  count: number;
  activeIndex: number;
  onDotPress: (i: number) => void;
}>) {
  const maxDots = 7;
  const showCondensed = count > maxDots;

  if (!showCondensed) {
    return (
      <HStack spacing="xs" justify="center" style={{ paddingTop: tokens.spacing.xs }}>
        {Array.from({ length: count }).map((_, i) => (
          <Pressable key={i} onPress={() => onDotPress(i)}>
            <Box
              width={activeIndex === i ? 20 : 8}
              height={8}
              borderRadius="full"
              backgroundColor={activeIndex === i ? colors.primary.main : colors.border.default}
            />
          </Pressable>
        ))}
      </HStack>
    );
  }

  return (
    <HStack spacing="xs" justify="center" align="center" style={{ paddingTop: tokens.spacing.xs }}>
      <Text variant="caption" color={colors.text.secondary} style={{ fontWeight: '500' }}>
        {activeIndex + 1} / {count}
      </Text>
    </HStack>
  );
}

export function LinkedInActivityCarouselCard({
  companyName,
  companyPosts,
  contacts,
  onRefresh,
  isRefreshing,
  onEnrichCommenter,
}: Readonly<LinkedInActivityCarouselCardProps>) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const slideOpacity = useRef(new Animated.Value(1)).current;
  const slideTranslateX = useRef(new Animated.Value(0)).current;

  const sourcePages = useMemo<SourcePage[]>(() => {
    const pages: SourcePage[] = [];

    if (companyPosts && companyPosts.length > 0) {
      pages.push({
        source: {
          type: 'company',
          name: companyName || 'Company',
          avatarName: companyName || 'C',
        },
        posts: [...companyPosts].sort(byPostRecencyDesc),
      });
    }

    if (contacts) {
      const primary = contacts.filter((c) => c.isPrimary);
      const others = contacts.filter((c) => !c.isPrimary);

      for (const contact of [...primary, ...others]) {
        if (contact.posts.length === 0) continue;
        pages.push({
          source: {
            type: 'contact',
            name: contact.name || 'Contact',
            avatarName: contact.name || '?',
            sourceUrl: contact.sourceUrl,
            isPrimary: contact.isPrimary,
          },
          posts: [...contact.posts].sort(byPostRecencyDesc),
        });
      }
    }

    return pages;
  }, [companyPosts, contacts, companyName]);

  const totalPosts = sourcePages.reduce((sum, p) => sum + p.posts.length, 0);
  useEffect(() => {
    if (activeIndex >= sourcePages.length && sourcePages.length > 0) {
      setActiveIndex(sourcePages.length - 1);
    }
  }, [sourcePages.length, activeIndex]);

  const goToIndex = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= sourcePages.length || idx === activeIndex) return;
      const direction = idx > activeIndex ? 1 : -1;
      Animated.timing(slideOpacity, {
        toValue: 0,
        duration: 120,
        useNativeDriver: true,
        easing: Easing.out(Easing.ease),
      }).start(() => {
        setActiveIndex(idx);
        slideTranslateX.setValue(direction * 40);
        Animated.parallel([
          Animated.timing(slideOpacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
            easing: Easing.out(Easing.cubic),
          }),
          Animated.timing(slideTranslateX, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
            easing: Easing.out(Easing.cubic),
          }),
        ]).start();
      });
    },
    [sourcePages.length, activeIndex, slideOpacity, slideTranslateX],
  );

  const getRefreshTarget = useCallback((): RefreshTarget => {
    const activePage = sourcePages[activeIndex];
    if (!activePage) return { type: 'all' };
    if (activePage.source.type === 'company') return { type: 'company' };
    if (activePage.source.sourceUrl) {
      return {
        type: 'contact',
        sourceUrl: activePage.source.sourceUrl,
        name: activePage.source.name,
      };
    }
    return { type: 'all' };
  }, [sourcePages, activeIndex]);

  if (sourcePages.length === 0) return null;

  const refreshTarget = getRefreshTarget();
  const activePage = sourcePages[activeIndex];
  const refreshLabel =
    refreshTarget.type === 'contact'
      ? refreshTarget.name
      : refreshTarget.type === 'company'
        ? (companyName ?? 'Company')
        : undefined;

  return (
    <Box style={styles.card}>
      <VStack spacing="sm">
        {/* Header */}
        <HStack justify="between" align="center">
          <HStack spacing="xs" align="center">
            {sourcePages.length > 1 && (
              <Pressable
                onPress={() => goToIndex(Math.max(0, activeIndex - 1))}
                disabled={activeIndex === 0}
                style={{ opacity: activeIndex === 0 ? 0.3 : 1 }}
              >
                <ChevronLeft size={20} color={colors.text.primary} strokeWidth={2} />
              </Pressable>
            )}
            <LinkedinIcon size={16} color={colors.accent.blue[500]} strokeWidth={2} />
            <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
              {t('leads.detail.linkedinPosts.title')}
            </Text>
            <Box style={styles.countBadge}>
              <Text
                variant="caption"
                color={colors.accent.blue[400]}
                style={{ fontWeight: '700', fontSize: 12, lineHeight: 16 }}
              >
                {totalPosts}
              </Text>
            </Box>
            {sourcePages.length > 1 && (
              <Pressable
                onPress={() => goToIndex(Math.min(sourcePages.length - 1, activeIndex + 1))}
                disabled={activeIndex === sourcePages.length - 1}
                style={{ opacity: activeIndex === sourcePages.length - 1 ? 0.3 : 1 }}
              >
                <ChevronRight size={20} color={colors.text.primary} strokeWidth={2} />
              </Pressable>
            )}
          </HStack>
          {onRefresh && (
            <Pressable
              onPress={() => onRefresh(refreshTarget)}
              disabled={isRefreshing}
              accessibilityLabel={t('leads.detail.linkedinPosts.refreshButton')}
              accessibilityRole="button"
              style={[styles.refreshButton, isRefreshing && { opacity: 0.6 }]}
            >
              <HStack spacing="xs" align="center">
                {isRefreshing ? (
                  <ActivityIndicator size="small" color={colors.text.inverse} />
                ) : (
                  <RefreshCw size={13} color={colors.text.inverse} strokeWidth={2} />
                )}
                <Text
                  variant="caption"
                  color={colors.text.inverse}
                  style={{ fontWeight: '600' }}
                  numberOfLines={1}
                >
                  {isRefreshing
                    ? t('leads.detail.linkedinPosts.refreshing')
                    : refreshLabel
                      ? `${t('leads.detail.linkedinPosts.refreshButton')} · ${refreshLabel}`
                      : t('leads.detail.linkedinPosts.refreshButton')}
                </Text>
              </HStack>
            </Pressable>
          )}
        </HStack>

        {/* Source tabs (when multiple pages) */}
        {sourcePages.length > 1 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <HStack spacing="xs">
              {sourcePages.map((page, i) => {
                const isActive = i === activeIndex;
                const isCompany = page.source.type === 'company';
                const activeColor = isCompany
                  ? colors.accent.amber[400]
                  : page.source.isPrimary
                    ? colors.status.success.main
                    : colors.accent.blue[400];

                return (
                  <Pressable
                    key={`${page.source.type}-${page.source.name}-${i}`}
                    onPress={() => goToIndex(i)}
                  >
                    {/* Selected is a fill, not a ring. Six ringed pills above
                        a bordered card is five borders too many. */}
                    <Box
                      style={[
                        styles.sourceTab,
                        isActive
                          ? { backgroundColor: colors.primary.muted }
                          : { backgroundColor: 'transparent' },
                      ]}
                    >
                      <HStack spacing="xs" align="center">
                        {isCompany ? (
                          <Building2
                            size={11}
                            color={isActive ? activeColor : colors.text.tertiary}
                            strokeWidth={2}
                          />
                        ) : (
                          <User
                            size={11}
                            color={isActive ? activeColor : colors.text.tertiary}
                            strokeWidth={2}
                          />
                        )}
                        <Text
                          variant="caption"
                          color={isActive ? activeColor : colors.text.tertiary}
                          style={{ fontWeight: '500', fontSize: 11 }}
                          numberOfLines={1}
                        >
                          {page.source.name}
                        </Text>
                        <Box
                          style={[
                            styles.tabPostCount,
                            isActive && { backgroundColor: activeColor },
                          ]}
                        >
                          <Text
                            variant="caption"
                            color={isActive ? colors.background.primary : colors.text.tertiary}
                            style={{ fontWeight: '700', fontSize: 9, lineHeight: 12 }}
                          >
                            {page.posts.length}
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                  </Pressable>
                );
              })}
            </HStack>
          </ScrollView>
        )}

        {/* Active source page */}
        {activePage && (
          <Animated.View
            style={{ opacity: slideOpacity, transform: [{ translateX: slideTranslateX }] }}
          >
            <Box style={styles.postContainer}>
              <SourcePageSlide page={activePage} onEnrichCommenter={onEnrichCommenter} />
            </Box>
          </Animated.View>
        )}

        {/* Pagination dots */}
        {sourcePages.length > 1 && (
          <PaginationDots
            count={sourcePages.length}
            activeIndex={activeIndex}
            onDotPress={goToIndex}
          />
        )}
      </VStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    ...cardSurface(),
    padding: tokens.spacing.lg,
  },
  // Level 2. A post lives inside the card's border; giving it one of its own
  // is what made this column read as boxes inside boxes.
  postContainer: {
    paddingVertical: tokens.spacing.sm,
  },
  sourceTab: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.full,
    minHeight: 28,
    justifyContent: 'center' as const,
    maxWidth: 160,
  },
  tabPostCount: {
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.border.subtle,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 4,
  },
  timelineScroll: {
    maxHeight: 600,
  },
  timelineTrack: {
    width: 20,
    alignItems: 'center' as const,
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary[400],
    marginTop: 4,
  },
  timelineLine: {
    width: 2,
    flex: 1,
    backgroundColor: colors.border.muted,
    marginTop: 4,
  },
  linkedinButton: {
    backgroundColor: colors.background.tertiary,
    borderRadius: tokens.radius.full,
    padding: 4,
  },
  countBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.brand.linkedinTint,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    paddingHorizontal: tokens.spacing.xs,
  },
  refreshButton: {
    backgroundColor: colors.accent.blue[500],
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
  },
});
